import { useState, useEffect, useCallback } from 'react'
import { useEditorStore } from '../../store/editor.store'
import { designSystemApi, type ApiDesignToken, type ApiDesignSystem, type TokenCategory } from '../../utils/api'

const CATEGORY_LABELS: Record<TokenCategory, string> = {
  COLOR: 'Цвета',
  TYPOGRAPHY: 'Типографика',
  SPACING: 'Отступы',
  BORDER_RADIUS: 'Скругление',
  SHADOW: 'Тени',
  MOTION: 'Анимации',
  BREAKPOINT: 'Точки разрыва',
  CUSTOM: 'Прочие',
}

const CATEGORY_ORDER: TokenCategory[] = [
  'COLOR', 'TYPOGRAPHY', 'SPACING', 'BORDER_RADIUS', 'SHADOW', 'MOTION', 'BREAKPOINT', 'CUSTOM',
]

const DEFAULT_VALUES: Record<TokenCategory, string> = {
  COLOR: '#2563eb',
  TYPOGRAPHY: 'Inter, sans-serif',
  SPACING: '16px',
  BORDER_RADIUS: '8px',
  SHADOW: '0 4px 24px rgba(0,0,0,0.12)',
  MOTION: '0.2s ease',
  BREAKPOINT: '768px',
  CUSTOM: '',
}

interface TokenEditorProps {
  token: ApiDesignToken
  projectId: string
  onUpdated: (token: ApiDesignToken) => void
  onDeleted: (id: string) => void
}

function TokenEditor({ token, projectId, onUpdated, onDeleted }: TokenEditorProps) {
  const [value, setValue] = useState(token.value)
  const [saving, setSaving] = useState(false)

  useEffect(() => { setValue(token.value) }, [token.value])

  async function save() {
    if (value === token.value) return
    setSaving(true)
    try {
      const updated = await designSystemApi.updateToken(projectId, token.id, { value: value.trim() })
      onUpdated(updated)
    } catch { /* silently fail on token update */ } finally {
      setSaving(false)
    }
  }

  async function remove() {
    if (!confirm(`Удалить токен "${token.key}"?`)) return
    try {
      await designSystemApi.deleteToken(projectId, token.id)
      onDeleted(token.id)
    } catch { /* silently fail on token delete */ }
  }

  const isColor = token.category === 'COLOR' && /^#[0-9a-f]{3,8}$/i.test(value)

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
      {isColor && (
        <input
          type="color"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          style={{ width: 28, height: 28, border: 'none', padding: 0, cursor: 'pointer', flexShrink: 0, borderRadius: 4 }}
          title="Выбрать цвет"
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'monospace', marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{token.key}</div>
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.currentTarget.blur() } }}
          style={{
            width: '100%', fontSize: 12, background: 'var(--bg-3)',
            border: '1px solid var(--border)', borderRadius: 4,
            padding: '2px 6px', color: 'var(--text-primary)',
            outline: 'none',
          }}
          disabled={saving}
        />
      </div>
      <button
        onClick={remove}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 16, padding: '0 2px', flexShrink: 0 }}
        title="Удалить токен"
      >×</button>
    </div>
  )
}

interface AddTokenFormProps {
  category: TokenCategory
  projectId: string
  onAdded: (token: ApiDesignToken) => void
}

function AddTokenForm({ category, projectId, onAdded }: AddTokenFormProps) {
  const [key, setKey] = useState('')
  const [value, setValue] = useState(DEFAULT_VALUES[category])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function add() {
    if (!key.trim()) { setError('Введите ключ токена'); return }
    setSaving(true)
    setError('')
    try {
      const token = await designSystemApi.createToken(projectId, {
        category, key: key.trim(), value: value.trim(),
      })
      onAdded(token)
      setKey('')
      setValue(DEFAULT_VALUES[category])
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Ошибка')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ padding: '8px 0', borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', gap: 4, marginBottom: 4 }}>
        <input
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder={`Ключ (напр. ${category === 'COLOR' ? 'color.brand' : 'spacing.lg'})`}
          style={{ flex: 1, fontSize: 11, background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 6px', color: 'var(--text-primary)' }}
          onKeyDown={(e) => { if (e.key === 'Enter') add() }}
        />
        <input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Значение"
          style={{ width: 80, fontSize: 11, background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 4, padding: '3px 6px', color: 'var(--text-primary)' }}
          onKeyDown={(e) => { if (e.key === 'Enter') add() }}
        />
        <button
          onClick={add}
          disabled={saving}
          style={{ background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 4, padding: '3px 8px', cursor: 'pointer', fontSize: 12, flexShrink: 0 }}
        >+</button>
      </div>
      {error && <div style={{ fontSize: 11, color: 'var(--color-error, #ef4444)' }}>{error}</div>}
    </div>
  )
}

interface CategorySectionProps {
  category: TokenCategory
  tokens: ApiDesignToken[]
  projectId: string
  onUpdated: (token: ApiDesignToken) => void
  onDeleted: (id: string) => void
  onAdded: (token: ApiDesignToken) => void
}

function CategorySection({ category, tokens, projectId, onUpdated, onDeleted, onAdded }: CategorySectionProps) {
  const [collapsed, setCollapsed] = useState(false)
  const [adding, setAdding] = useState(false)

  return (
    <div style={{ marginBottom: 8 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 0', cursor: 'pointer' }}
        onClick={() => setCollapsed(!collapsed)}
      >
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-muted)' }}>
          {collapsed ? '▶' : '▼'} {CATEGORY_LABELS[category]} ({tokens.length})
        </span>
        <button
          onClick={(e) => { e.stopPropagation(); setAdding(!adding) }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 14, padding: '0 2px' }}
          title="Добавить токен"
        >+</button>
      </div>
      {!collapsed && (
        <>
          {tokens.map((t) => (
            <TokenEditor key={t.id} token={t} projectId={projectId} onUpdated={onUpdated} onDeleted={onDeleted} />
          ))}
          {adding && (
            <AddTokenForm
              category={category}
              projectId={projectId}
              onAdded={(token) => { onAdded(token); setAdding(false) }}
            />
          )}
        </>
      )}
    </div>
  )
}

export function DesignTokensPanel() {
  const projectId = useEditorStore((s) => s.projectId)
  const [ds, setDs] = useState<ApiDesignSystem | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError('')
    try {
      const data = await designSystemApi.get(projectId)
      setDs(data)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Ошибка загрузки')
    } finally {
      setLoading(false)
    }
  }, [projectId])

  useEffect(() => { void load() }, [load])

  const handleUpdated = useCallback((updated: ApiDesignToken) => {
    setDs((prev) => prev ? {
      ...prev,
      tokens: prev.tokens.map((t) => t.id === updated.id ? updated : t),
    } : prev)
  }, [])

  const handleDeleted = useCallback((id: string) => {
    setDs((prev) => prev ? { ...prev, tokens: prev.tokens.filter((t) => t.id !== id) } : prev)
  }, [])

  const handleAdded = useCallback((token: ApiDesignToken) => {
    setDs((prev) => prev ? { ...prev, tokens: [...prev.tokens, token] } : prev)
  }, [])

  if (!projectId) {
    return (
      <div style={{ padding: '20px 12px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 12 }}>
        <div style={{ fontSize: 20, marginBottom: 8 }}>🎨</div>
        <div>Откройте проект для работы с Design System</div>
      </div>
    )
  }

  if (loading) {
    return <div style={{ padding: 12, color: 'var(--text-muted)', fontSize: 12 }}>Загрузка токенов...</div>
  }

  if (error) {
    return (
      <div style={{ padding: 12 }}>
        <div style={{ color: 'var(--color-error, #ef4444)', fontSize: 12, marginBottom: 8 }}>{error}</div>
        <button onClick={load} style={{ fontSize: 12, padding: '4px 8px', background: 'var(--bg-3)', border: '1px solid var(--border)', borderRadius: 4, cursor: 'pointer' }}>
          Повторить
        </button>
      </div>
    )
  }

  if (!ds) return null

  const byCategory = CATEGORY_ORDER.reduce<Record<TokenCategory, ApiDesignToken[]>>((acc, cat) => {
    acc[cat] = ds.tokens.filter((t) => t.category === cat)
    return acc
  }, {} as Record<TokenCategory, ApiDesignToken[]>)

  const hasTokens = ds.tokens.length > 0

  return (
    <div style={{ padding: '8px 12px', overflowY: 'auto', height: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>{ds.name}</span>
        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{ds.tokens.length} токенов</span>
      </div>

      {!hasTokens && (
        <div style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: 12, padding: '16px 0' }}>
          Нет токенов — добавьте первый через категорию ниже
        </div>
      )}

      {CATEGORY_ORDER.map((cat) => (
        <CategorySection
          key={cat}
          category={cat}
          tokens={byCategory[cat]}
          projectId={projectId}
          onUpdated={handleUpdated}
          onDeleted={handleDeleted}
          onAdded={handleAdded}
        />
      ))}
    </div>
  )
}
